import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Skull,
    Smile,
    X,
    ShieldCheck,
    LockKeyhole,
    Sparkles,
    CheckCircle2,
    AlertTriangle,
} from "lucide-react";

import api from "../../api/axios";

const ChangePasswordModal = ({
    open,
    onClose,
    onSuccess,
}) => {

    const [
        currentPassword,
        setCurrentPassword,
    ] = useState("");

    const [
        newPassword,
        setNewPassword,
    ] = useState("");

    const [
        confirmPassword,
        setConfirmPassword,
    ] = useState("");

    const [
        showCurrentPassword,
        setShowCurrentPassword,
    ] = useState(false);

    const [
        showNewPassword,
        setShowNewPassword,
    ] = useState(false);

    const [
        showConfirmPassword,
        setShowConfirmPassword,
    ] = useState(false);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState("");

    // ================= RESET =================
    const resetFields = useCallback(() => {

        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        setError("");
        setSuccess("");
    }, []);

    // ================= CLOSE =================
    const handleClose = useCallback(() => {

        if (loading)
            return;

        resetFields();

        onClose();
    }, [loading, onClose, resetFields]);

    // ================= BODY LOCK =================
    useEffect(() => {

        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };

    }, [open]);

    // ================= ESC CLOSE =================
    useEffect(() => {

        const handleEsc = (e) => {

            if (
                e.key === "Escape" &&
                !loading
            ) {
                handleClose();
            }
        };

        if (open) {
            window.addEventListener(
                "keydown",
                handleEsc,
            );
        }

        return () => {
            window.removeEventListener(
                "keydown",
                handleEsc,
            );
        };

    }, [open, loading, handleClose]);

    // ================= PASSWORD RULE =================
    const strongPassword =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

    // ================= PASSWORD STRENGTH =================
    const passwordStrength =
        useMemo(() => {

            let score = 0;

            if (
                newPassword.length >= 8
            ) score++;

            if (
                /[A-Z]/.test(
                    newPassword,
                )
            ) score++;

            if (
                /[a-z]/.test(
                    newPassword,
                )
            ) score++;

            if (
                /\d/.test(
                    newPassword,
                )
            ) score++;

            if (
                /[@$!%*?&]/.test(
                    newPassword,
                )
            ) score++;

            if (score <= 2)
                return {
                    label: "Weak",
                    width: "35%",
                    color: "bg-red-500",
                };

            if (score <= 4)
                return {
                    label: "Medium",
                    width: "70%",
                    color: "bg-yellow-500",
                };

            return {
                label: "Strong",
                width: "100%",
                color: "bg-emerald-500",
            };

        }, [newPassword]);

    if (!open) return null;

    // ================= SUBMIT =================
    const handleSubmit = async (e) => {

        e.preventDefault();

        if (loading)
            return;

        setError("");
        setSuccess("");

        if (
            !currentPassword.trim() ||
            !newPassword.trim() ||
            !confirmPassword.trim()
        ) {
            setError(
                "All fields are required",
            );

            return;
        }

        if (
            currentPassword ===
            newPassword
        ) {
            setError(
                "New password cannot be same as current password",
            );

            return;
        }

        if (
            newPassword !==
            confirmPassword
        ) {
            setError(
                "Passwords do not match",
            );

            return;
        }

        if (
            !strongPassword.test(
                newPassword,
            )
        ) {
            setError(
                "Password does not meet security requirements",
            );

            return;
        }

        try {

            setLoading(true);

            const res =
                await api.put(
                    "/api/employee/profile/change-password",
                    {
                        currentPassword,
                        newPassword,
                        confirmPassword,
                    },
                );

            setSuccess(
                res.data.message ||
                "Password changed successfully",
            );

            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");

            setTimeout(() => {

                if (onSuccess) {
                    onSuccess();
                }

            }, 1800);

        } catch (err) {

            setError(
                err.response?.data
                    ?.message ||
                "Failed to change password",
            );

        } finally {

            setLoading(false);
        }
    };

    return (
        <div
            className="
            fixed
            inset-0

            z-[9999]

            overflow-y-auto

            bg-black/70
            backdrop-blur-md

            p-4
            md:p-8
        "
        >

            {/* CENTER WRAPPER */}
            <div
                className="
                min-h-full

                flex
                items-center
                justify-center
            "
            >

                {/* MODAL */}
                <div
                    className="
                    relative

                    w-full
                    max-w-xl

                    rounded-[32px]

                    border
                    border-[var(--border-color)]

                    bg-white/95
                    dark:bg-[#18181b]/95

                    backdrop-blur-xl

                    shadow-2xl

                    overflow-hidden
                "
                >

                    {/* GLOW */}
                    <div
                        className="
                        absolute

                        top-0
                        right-0

                        w-56
                        h-56

                        bg-[var(--accent-primary)]/10

                        blur-3xl

                        rounded-full
                    "
                    />

                    {/* CLOSE */}
                    <button
                        onClick={
                            handleClose
                        }
                        disabled={
                            loading
                        }
                        className="
                        absolute
                        top-5
                        right-5

                        w-10
                        h-10

                        rounded-2xl

                        bg-[var(--bg-secondary)]
                        hover:bg-black/5
                        dark:hover:bg-white/5

                        border
                        border-[var(--border-color)]

                        flex
                        items-center
                        justify-center

                        transition-all

                        z-20
                    "
                    >
                        <X size={18} />
                    </button>

                    {/* CONTENT */}
                    <div
                        className="
                        relative

                        p-6
                        md:p-8
                    "
                    >

                        {/* HEADER */}
                        <div
                            className="
                            flex
                            items-start

                            gap-5

                            mb-8
                        "
                        >

                            <div
                                className="
                                w-16
                                h-16

                                rounded-3xl

                                bg-[var(--accent-primary)]

                                flex
                                items-center
                                justify-center

                                text-black
                            "
                            >
                                <ShieldCheck
                                    size={28}
                                />
                            </div>

                            <div>

                                <h2
                                    className="
                                    text-2xl
                                    md:text-3xl

                                    font-bold
                                "
                                >
                                    Change Password
                                </h2>

                                <p
                                    className="
                                    mt-2

                                    text-sm
                                    md:text-base

                                    text-[var(--text-secondary)]
                                "
                                >
                                    Choose a strong password that you do not use for other accounts.
                                </p>

                            </div>

                        </div>

                        {/* ERROR */}
                        {error && (
                            <AlertBox
                                type="error"
                                text={error}
                            />
                        )}

                        {/* SUCCESS */}
                        {success && (
                            <AlertBox
                                type="success"
                                text={success}
                            />
                        )}

                        {!success && (
                            <form
                                onSubmit={
                                    handleSubmit
                                }
                                className="space-y-6"
                            >

                                <PasswordField
                                    label="Current Password"
                                    value={
                                        currentPassword
                                    }
                                    onChange={(
                                        e,
                                    ) =>
                                        setCurrentPassword(
                                            e
                                                .target
                                                .value,
                                        )
                                    }
                                    show={
                                        showCurrentPassword
                                    }
                                    toggle={() =>
                                        setShowCurrentPassword(
                                            !showCurrentPassword,
                                        )
                                    }
                                    placeholder="Enter current password"
                                />

                                <div>

                                    <PasswordField
                                        label="New Password"
                                        value={
                                            newPassword
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setNewPassword(
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                        show={
                                            showNewPassword
                                        }
                                        toggle={() =>
                                            setShowNewPassword(
                                                !showNewPassword,
                                            )
                                        }
                                        placeholder="Enter new password"
                                    />

                                    {newPassword && (
                                        <div className="mt-4">

                                            <div
                                                className="
                                                flex
                                                items-center
                                                justify-between

                                                mb-2
                                            "
                                            >

                                                <p
                                                    className="
                                                    text-xs

                                                    text-[var(--text-secondary)]
                                                "
                                                >
                                                    Password Strength
                                                </p>

                                                <p
                                                    className="
                                                    text-xs
                                                    font-medium
                                                "
                                                >
                                                    {
                                                        passwordStrength.label
                                                    }
                                                </p>

                                            </div>

                                            <div
                                                className="
                                                h-2

                                                rounded-full

                                                bg-[var(--bg-secondary)]

                                                overflow-hidden
                                            "
                                            >

                                                <div
                                                    className={`
h - full

transition - all

                                                    ${passwordStrength.color}
`}
                                                    style={{
                                                        width:
                                                            passwordStrength.width,
                                                    }}
                                                />

                                            </div>

                                        </div>
                                    )}

                                </div>

                                <PasswordField
                                    label="Confirm Password"
                                    value={
                                        confirmPassword
                                    }
                                    onChange={(
                                        e,
                                    ) =>
                                        setConfirmPassword(
                                            e
                                                .target
                                                .value,
                                        )
                                    }
                                    show={
                                        showConfirmPassword
                                    }
                                    toggle={() =>
                                        setShowConfirmPassword(
                                            !showConfirmPassword,
                                        )
                                    }
                                    placeholder="Confirm new password"
                                />

                                {/* RULES */}
                                <div
                                    className="
                                    bg-[var(--bg-secondary)]

                                    border
                                    border-[var(--border-color)]

                                    rounded-3xl

                                    p-5
                                "
                                >

                                    <div
                                        className="
                                        flex
                                        items-center

                                        gap-2

                                        mb-4
                                    "
                                    >

                                        <Sparkles
                                            size={18}
                                            className="text-[var(--accent-primary)]"
                                        />

                                        <p className="font-semibold">
                                            Password Requirements
                                        </p>

                                    </div>

                                    <div
                                        className="
                                        grid
                                        grid-cols-1
                                        sm:grid-cols-2

                                        gap-3
                                    "
                                    >

                                        <Rule
                                            ok={
                                                newPassword.length >= 8
                                            }
                                            text="Minimum 8 characters"
                                        />

                                        <Rule
                                            ok={/[A-Z]/.test(
                                                newPassword,
                                            )}
                                            text="One uppercase letter"
                                        />

                                        <Rule
                                            ok={/[a-z]/.test(
                                                newPassword,
                                            )}
                                            text="One lowercase letter"
                                        />

                                        <Rule
                                            ok={/\d/.test(
                                                newPassword,
                                            )}
                                            text="One number"
                                        />

                                        <Rule
                                            ok={/[@$!%*?&]/.test(
                                                newPassword,
                                            )}
                                            text="One special character"
                                        />

                                    </div>

                                </div>

                                {/* BUTTON */}
                                <button
                                    type="submit"
                                    disabled={
                                        loading
                                    }
                                    className="
                                    w-full

                                    inline-flex
                                    items-center
                                    justify-center

                                    gap-3

                                    bg-[var(--accent-primary)]
                                    hover:bg-[var(--accent-hover)]

                                    text-white

                                    py-4

                                    rounded-2xl

                                    font-semibold

                                    transition-all

                                    disabled:opacity-60
                                "
                                >

                                    <LockKeyhole
                                        size={18}
                                    />

                                    {loading
                                        ? "Updating..."
                                        : "Change Password"}

                                </button>

                            </form>
                        )}

                    </div>

                </div>

            </div>

        </div>
    );
};

// ================= PASSWORD FIELD =================
const PasswordField = ({
    label,
    value,
    onChange,
    show,
    toggle,
    placeholder,
}) => {

    return (
        <div>

            <label
                className="
                block

                text-sm
                font-medium

                mb-3
            "
            >
                {label}
            </label>

            <div className="relative">

                <input
                    type={
                        show
                            ? "text"
                            : "password"
                    }
                    value={value}
                    onChange={
                        onChange
                    }
                    placeholder={
                        placeholder
                    }
                    className="
                    w-full

                    bg-[var(--bg-secondary)]

                    border
                    border-[var(--border-color)]

                    px-5
                    py-4
                    pr-14

                    rounded-2xl

                    outline-none

                    transition-all

                    focus:border-[var(--accent-primary)]
                "
                    required
                />

                <button
                    type="button"
                    onClick={toggle}
                    className="
                    absolute

                    right-4
                    top-1/2
                    -translate-y-1/2

                    text-[var(--text-secondary)]
                    hover:text-[var(--accent-primary)]

                    transition-all
                "
                >

                    {show ? (
                        <Smile size={20} />
                    ) : (
                        <Skull size={20} />
                    )}

                </button>

            </div>

        </div>
    );
};

// ================= ALERT =================
const AlertBox = ({
    type,
    text,
}) => {

    const isError =
        type === "error";

    return (
        <div
            className={`
mb - 5

flex
items - start

gap - 3

border

rounded - 2xl

px - 5
py - 4

            ${isError
                    ? "bg-red-500/10 border-red-500/20 text-red-400"
                    : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                }
`}
        >

            {isError ? (
                <AlertTriangle
                    size={18}
                />
            ) : (
                <CheckCircle2
                    size={18}
                />
            )}

            <p
                className="
                text-sm
                leading-6
            "
            >
                {text}
            </p>

        </div>
    );
};

// ================= RULE =================
const Rule = ({
    ok,
    text,
}) => {

    return (
        <div
            className="
            flex
            items-center

            gap-2
        "
        >

            <div
                className={`
w - 2.5
h - 2.5

rounded - full

                ${ok
                        ? "bg-emerald-500"
                        : "bg-gray-500"
                    }
`}
            />

            <p
                className={`
text - sm

                ${ok
                        ? "text-emerald-400"
                        : "text-[var(--text-secondary)]"
                    }
`}
            >
                {text}
            </p>

        </div>
    );
};

export default ChangePasswordModal;
